import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { produce } from 'immer';
import {
  SButton,
  SIcon,
  SInput,
  SModal,
  SNotification,
  SRadioGroup,
  SSelect,
  STable,
  STooltip,
} from '@chargebee/sting-react';
import { ConditionsGroup } from '@bbkAdminComponents/experiences/audiences/edit-rule/conditions-group';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  OptimizationActionType,
  OptimizationAddonAdjustmentType,
  OptimizationAddonScope,
  OptimizationPlanScope,
  OptimizationPriceAdjustmentType,
  OptimizationSliceHelper,
  optimizationsSlice,
  type OptimizationAction,
} from '@bbkAdminRedux/rtkq/optimizations.slice';
import { cs } from '@bbkAdminUtils/css-utils';
import {
  createActionTypeSwitchChange,
  createAddonScopeSwitchAction,
  createPlanScopeSwitchChange,
  getAddonScopeForUi,
  getPlanScopeForUi,
  isAddonPercentageAdjustmentType,
  isPlanPercentageAdjustmentType,
  type PendingActionChange,
} from './OptimizationActionForm.helpers';
import { OptimizationActionsHelper } from './OptimizationActionsHelper';

type OptimizationActionFormProps = {
  value: OptimizationAction;
  onChange: (action: OptimizationAction) => void;
  actionsOnly?: boolean;
};

type PlanPriceAdjustmentAction = Extract<
  OptimizationAction,
  { type: OptimizationActionType }
> & {
  type: OptimizationActionType.PLAN_PRICE_ADJUSTMENT;
  scope?: OptimizationPlanScope;
  adjustment_type?: OptimizationPriceAdjustmentType;
  adjustment_value?: number;
  plan_adjustments?: Array<{
    plan_id: string;
    adjustment_type: OptimizationPriceAdjustmentType;
    adjustment_value: number;
  }>;
};

type AddonPriceAdjustmentAction = Extract<
  OptimizationAction,
  { type: OptimizationActionType }
> & {
  type: OptimizationActionType.ADDON_PRICE_ADJUSTMENT;
  scope?: OptimizationAddonScope;
  adjustment_type?: OptimizationAddonAdjustmentType;
  adjustment_value?: number;
  addons?: Array<{
    item_price_id: string;
    adjustment_type: OptimizationAddonAdjustmentType;
    adjustment_value: number;
  }>;
};

const addonAdjustmentTypeOptions = [
  {
    label: 'Increase by percentage',
    value: OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE,
  },
  {
    label: 'Increase by fixed amount',
    value: OptimizationAddonAdjustmentType.INCREASE_BY_FIXED_AMOUNT,
  },
  {
    label: 'Decrease by percentage',
    value: OptimizationAddonAdjustmentType.DECREASE_BY_PERCENTAGE,
  },
  {
    label: 'Decrease by fixed amount',
    value: OptimizationAddonAdjustmentType.DECREASE_BY_FIXED_AMOUNT,
  },
];

const basePlanAdjustmentTypeOptions = [
  {
    label: 'Increase by percentage',
    value: OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE,
  },
  {
    label: 'Increase by fixed amount',
    value: OptimizationPriceAdjustmentType.INCREASE_BY_FIXED_AMOUNT,
  },
  {
    label: 'Decrease by percentage',
    value: OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE,
  },
  {
    label: 'Decrease by fixed amount',
    value: OptimizationPriceAdjustmentType.DECREASE_BY_FIXED_AMOUNT,
  },
];

const getPlanAdjustmentTypeOptions = (adjustmentType: string | undefined) =>
  adjustmentType === OptimizationPriceAdjustmentType.SET_FIXED_PRICE
    ? [
        ...basePlanAdjustmentTypeOptions,
        {
          label: 'Set fixed price',
          value: OptimizationPriceAdjustmentType.SET_FIXED_PRICE,
        },
      ]
    : basePlanAdjustmentTypeOptions;

/** Chargebee catalog prices are in the smallest currency unit (e.g. cents). */
const catalogPriceToMajorUnits = (price: number): number => price / 100;

const formatPriceCell = (
  majorUnits: number | undefined,
  currencyCode: string | undefined
): string => {
  if (majorUnits === undefined || !Number.isFinite(majorUnits)) {
    return '—';
  }

  const cur = currencyCode?.trim() ?? '';
  const value = majorUnits.toFixed(2);
  return cur ? `${cur} ${value}` : value;
};

const previewNewPriceMajorUnits = (
  currentMajor: number | undefined,
  adjustmentType: string | undefined,
  adjustmentValue: number | undefined
): number | undefined => {
  if (currentMajor === undefined || !Number.isFinite(currentMajor)) {
    return undefined;
  }

  const value = adjustmentValue ?? 0;
  switch (adjustmentType) {
    case OptimizationPriceAdjustmentType.INCREASE_BY_PERCENTAGE:
    case OptimizationAddonAdjustmentType.INCREASE_BY_PERCENTAGE:
      return currentMajor * (1 + value / 100);
    case OptimizationPriceAdjustmentType.DECREASE_BY_PERCENTAGE:
    case OptimizationAddonAdjustmentType.DECREASE_BY_PERCENTAGE:
      return currentMajor * (1 - value / 100);
    case OptimizationPriceAdjustmentType.INCREASE_BY_FIXED_AMOUNT:
    case OptimizationAddonAdjustmentType.INCREASE_BY_FIXED_AMOUNT:
      return currentMajor + value;
    case OptimizationPriceAdjustmentType.DECREASE_BY_FIXED_AMOUNT:
    case OptimizationAddonAdjustmentType.DECREASE_BY_FIXED_AMOUNT:
      return currentMajor - value;
    case OptimizationPriceAdjustmentType.SET_FIXED_PRICE:
      return value;
    default:
      return undefined;
  }
};

export const OptimizationActionForm: React.FC<OptimizationActionFormProps> = ({
  value: action,
  onChange,
  actionsOnly,
}) => {
  const appKey = useSelector(selectAppId);
  const [pendingActionChange, setPendingActionChange] =
    useState<PendingActionChange | null>(null);

  const { data: plans = [] } = optimizationsSlice.endpoints.getPlans.useQuery(
    appKey ? { appKey } : skipToken
  );

  const { data: addons = [] } = optimizationsSlice.endpoints.getAddons.useQuery(
    appKey ? { appKey } : skipToken
  );

  const allPlansOptions = useMemo(
    () => OptimizationActionsHelper.getPlanOptions(plans),
    [plans]
  );

  const allAddonOptions = useMemo(
    () => OptimizationActionsHelper.getAddonOptions(addons),
    [addons]
  );

  const planCatalogById = useMemo(() => {
    const catalog = new Map(plans.map((plan) => [plan.id, plan]));
    return (id: string | undefined) => (id ? catalog.get(id) : undefined);
  }, [plans]);

  const addonCatalogById = useMemo(() => {
    const catalog = new Map(addons.map((addon) => [addon.id, addon]));
    return (id: string | undefined) => (id ? catalog.get(id) : undefined);
  }, [addons]);

  const planScopeForUi =
    action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT
      ? getPlanScopeForUi(action as PlanPriceAdjustmentAction)
      : null;

  const addonScopeForUi =
    action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT
      ? getAddonScopeForUi(action as AddonPriceAdjustmentAction)
      : null;

  const isPercentageAdj =
    (action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT &&
      planScopeForUi === OptimizationPlanScope.ALL_PLANS &&
      isPlanPercentageAdjustmentType(action.adjustment_type)) ||
    (action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT &&
      addonScopeForUi === OptimizationAddonScope.ALL_ADDONS &&
      isAddonPercentageAdjustmentType(action.adjustment_type));

  const handleConfirmPendingActionChange = () => {
    if (pendingActionChange) {
      onChange(pendingActionChange.nextAction);
    }
    setPendingActionChange(null);
  };

  const handleCancelPendingActionChange = () => {
    setPendingActionChange(null);
  };

  const handlePendingActionModalOpenChange = (open: boolean) => {
    if (!open) {
      setPendingActionChange(null);
    }
  };

  const renderMenu = (idx: number) => {
    const hasOnlyOnePlan =
      action.type === OptimizationActionType.PLAN_CHANGE &&
      (action.plans?.length ?? 0) <= 1;

    const handleRemove = () => {
      if (action.type !== OptimizationActionType.PLAN_CHANGE) return;
      onChange(
        produce(action, (draft) => {
          draft.plans?.splice(idx, 1);
        })
      );
    };

    const trashButton = (
      <SButton
        variant="danger-ghost"
        icon={<SIcon name="trash-2" />}
        onClick={handleRemove}
        disabled={hasOnlyOnePlan}
        className="!tw-border-transparent"
      />
    );

    return hasOnlyOnePlan ? (
      <STooltip
        label="At least one plan must remain"
        delayDuration={400}
        placement="left"
        color="light"
      >
        <div>{trashButton}</div>
      </STooltip>
    ) : (
      trashButton
    );
  };

  const renderPlanAdjustmentMenu = (idx: number) => {
    const hasOnlyOnePlanAdjustment =
      action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT &&
      planScopeForUi === OptimizationPlanScope.SPECIFIC_PLANS &&
      (action.plan_adjustments?.length ?? 0) <= 1;

    const handleRemove = () => {
      if (
        action.type !== OptimizationActionType.PLAN_PRICE_ADJUSTMENT ||
        planScopeForUi !== OptimizationPlanScope.SPECIFIC_PLANS
      ) {
        return;
      }

      onChange(
        produce(action, (draft) => {
          draft.plan_adjustments?.splice(idx, 1);
        })
      );
    };

    const trashButton = (
      <SButton
        variant="danger-ghost"
        icon={<SIcon name="trash-2" />}
        onClick={handleRemove}
        disabled={hasOnlyOnePlanAdjustment}
        className="!tw-border-transparent"
      />
    );

    return hasOnlyOnePlanAdjustment ? (
      <STooltip
        label="At least one plan must remain"
        delayDuration={400}
        placement="left"
        color="light"
      >
        <div>{trashButton}</div>
      </STooltip>
    ) : (
      trashButton
    );
  };

  const renderAddonMenu = (idx: number) => {
    const hasOnlyOneAddon =
      action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT &&
      (action.addons?.length ?? 0) <= 1;

    const handleRemove = () => {
      if (action.type !== OptimizationActionType.ADDON_PRICE_ADJUSTMENT) return;
      onChange(
        produce(action, (draft) => {
          draft.addons?.splice(idx, 1);
        })
      );
    };

    const trashButton = (
      <SButton
        variant="danger-ghost"
        icon={<SIcon name="trash-2" />}
        onClick={handleRemove}
        disabled={hasOnlyOneAddon}
        className="!tw-border-transparent"
      />
    );

    return hasOnlyOneAddon ? (
      <STooltip
        label="At least one addon must remain"
        delayDuration={400}
        placement="left"
        color="light"
      >
        <div>{trashButton}</div>
      </STooltip>
    ) : (
      trashButton
    );
  };

  const renderPlanScopeRadios = () => {
    if (action.type !== OptimizationActionType.PLAN_PRICE_ADJUSTMENT) {
      return null;
    }

    return (
      <div className="tw-col-span-2">
        <SRadioGroup
          className="[&_.radio-option-label]:!tw-m-0"
          value={planScopeForUi ?? OptimizationPlanScope.ALL_PLANS}
          orientation="horizontal"
          onValueChange={(val) => {
            const nextScope = val as OptimizationPlanScope;
            if (nextScope === planScopeForUi) {
              return;
            }

            setPendingActionChange(
              createPlanScopeSwitchChange(
                action as PlanPriceAdjustmentAction,
                nextScope
              )
            );
          }}
          options={[
            {
              value: OptimizationPlanScope.ALL_PLANS,
              label: 'All Plans',
            },
            {
              value: OptimizationPlanScope.SPECIFIC_PLANS,
              label: 'Specific Plans',
            },
          ]}
        />
      </div>
    );
  };

  const renderPlanAllFields = () => {
    if (
      action.type !== OptimizationActionType.PLAN_PRICE_ADJUSTMENT ||
      planScopeForUi !== OptimizationPlanScope.ALL_PLANS
    ) {
      return null;
    }

    return (
      <>
        <div>
          <SSelect
            className="tw-w-full"
            value={action.adjustment_type}
            onValueChange={(val) => {
              onChange(
                produce(action, (draft) => {
                  draft.adjustment_type =
                    val as unknown as OptimizationPriceAdjustmentType;
                })
              );
            }}
            clearable={false}
            searchable={false}
            options={getPlanAdjustmentTypeOptions(action.adjustment_type)}
            label="Plan Adjustment Type"
          />
        </div>
        <div>
          <SInput
            className="tw-w-full"
            onChange={(e) => {
              const val = parseFloat(e.currentTarget.value);

              onChange(
                produce(action, (draft) => {
                  draft.adjustment_value =
                    Number.isNaN(val) || val < 0 ? 0 : val;
                })
              );
            }}
            value={`${action.adjustment_value ?? 0}`}
            label={isPercentageAdj ? 'Percentage' : 'Adjustment amount'}
            type="number"
            trailingIcon={isPercentageAdj ? 'percent' : undefined}
            append={
              !isPercentageAdj ? (
                <span className="tw-text-sm tw-text-neutral-400">
                  per subscription currency
                </span>
              ) : undefined
            }
          />
        </div>
        {!isPercentageAdj && (
          <div className="tw-col-span-2">
            <SNotification
              icon={null}
              title="Amount is applied in each subscription's billing currency. For example, 10 becomes $10 for USD plans, EUR 10 for EUR plans, etc."
              variant="warning"
            />
          </div>
        )}
      </>
    );
  };

  const renderPlanSpecificTable = () => {
    if (
      action.type !== OptimizationActionType.PLAN_PRICE_ADJUSTMENT ||
      planScopeForUi !== OptimizationPlanScope.SPECIFIC_PLANS
    ) {
      return null;
    }

    const hasFixedAmountRows =
      action.plan_adjustments?.some(
        (row) =>
          row.adjustment_type !== undefined &&
          !isPlanPercentageAdjustmentType(row.adjustment_type)
      ) ?? false;

    return (
      <>
        <div className="tw-col-span-2">
          <STable layout="fixed">
            <STable.Head>
              <STable.Row>
                <STable.HeaderCell className="tw-w-2/12">
                  Plan ID
                </STable.HeaderCell>
                <STable.HeaderCell className="tw-w-2/12">
                  Adjustment Type
                </STable.HeaderCell>
                <STable.HeaderCell className="tw-w-2/12">
                  Value
                </STable.HeaderCell>
                <STable.HeaderCell className="tw-w-2/12">
                  Current Price
                </STable.HeaderCell>
                <STable.HeaderCell className="tw-w-2/12">
                  New Price
                </STable.HeaderCell>
                <STable.HeaderCell className="tw-w-1/12" />
              </STable.Row>
            </STable.Head>
            <STable.Body>
              {action.plan_adjustments?.map((row, idx) => {
                const rowIsPercentage = isPlanPercentageAdjustmentType(
                  row.adjustment_type
                );
                const planIdsTakenByOtherRows = new Set(
                  action.plan_adjustments
                    ?.filter((_, i) => i !== idx)
                    .map((planAdjustment) => planAdjustment.plan_id)
                    .filter((id): id is string => Boolean(id)) ?? []
                );

                let rowOptions = allPlansOptions.filter(
                  (option) =>
                    option.value === row.plan_id ||
                    !planIdsTakenByOtherRows.has(option.value)
                );

                if (
                  row.plan_id &&
                  !allPlansOptions.some(
                    (option) => option.value === row.plan_id
                  )
                ) {
                  rowOptions = [
                    {
                      value: row.plan_id,
                      label: row.plan_id,
                      currencyCode: '',
                    },
                    ...rowOptions,
                  ];
                }

                const catalogRow = planCatalogById(row.plan_id);
                const currentMajor =
                  catalogRow !== undefined
                    ? catalogPriceToMajorUnits(Number(catalogRow.price))
                    : undefined;
                const newMajor = previewNewPriceMajorUnits(
                  currentMajor,
                  row.adjustment_type,
                  row.adjustment_value
                );

                return (
                  <STable.Row key={idx}>
                    <STable.Cell>
                      <SSelect
                        options={rowOptions}
                        value={row.plan_id}
                        onValueChange={(val) => {
                          onChange(
                            produce(action, (draft) => {
                              draft.plan_adjustments ??= [];
                              draft.plan_adjustments[idx] ??=
                                OptimizationSliceHelper.createNewPlanAdjustment();
                              draft.plan_adjustments[idx].plan_id =
                                val as string;
                            })
                          );
                        }}
                      />
                    </STable.Cell>
                    <STable.Cell>
                      <SSelect
                        options={getPlanAdjustmentTypeOptions(
                          row.adjustment_type
                        )}
                        value={row.adjustment_type}
                        clearable={false}
                        searchable={false}
                        onValueChange={(val) => {
                          onChange(
                            produce(action, (draft) => {
                              draft.plan_adjustments ??= [];
                              draft.plan_adjustments[idx] ??=
                                OptimizationSliceHelper.createNewPlanAdjustment();
                              draft.plan_adjustments[idx].adjustment_type =
                                val as unknown as OptimizationPriceAdjustmentType;
                            })
                          );
                        }}
                      />
                    </STable.Cell>
                    <STable.Cell>
                      <SInput
                        onChange={(e) => {
                          const val = parseFloat(e.currentTarget.value);
                          onChange(
                            produce(action, (draft) => {
                              draft.plan_adjustments ??= [];
                              draft.plan_adjustments[idx] ??=
                                OptimizationSliceHelper.createNewPlanAdjustment();
                              draft.plan_adjustments[idx].adjustment_value =
                                Number.isNaN(val) || val < 0 ? 0 : val;
                            })
                          );
                        }}
                        value={`${row.adjustment_value ?? 0}`}
                        type="number"
                        trailingIcon={rowIsPercentage ? 'percent' : undefined}
                      />
                    </STable.Cell>
                    <STable.Cell>
                      <span className="tw-text-sm tw-text-neutral-800">
                        {formatPriceCell(
                          currentMajor,
                          catalogRow?.currency_code
                        )}
                      </span>
                    </STable.Cell>
                    <STable.Cell>
                      <span className="tw-text-sm tw-text-neutral-800">
                        {formatPriceCell(newMajor, catalogRow?.currency_code)}
                      </span>
                    </STable.Cell>
                    <STable.Cell>{renderPlanAdjustmentMenu(idx)}</STable.Cell>
                  </STable.Row>
                );
              })}
            </STable.Body>
            <STable.Footer>
              <STable.Row>
                <STable.Cell colSpan={5}>
                  <SButton
                    variant="primary-ghost"
                    icon={<SIcon name="plus" />}
                    onClick={() => {
                      onChange(
                        produce(action, (draft) => {
                          draft.plan_adjustments ??= [];
                          draft.plan_adjustments[
                            draft.plan_adjustments.length
                          ] = OptimizationSliceHelper.createNewPlanAdjustment();
                        })
                      );
                    }}
                  >
                    Add Another Plan
                  </SButton>
                </STable.Cell>
              </STable.Row>
            </STable.Footer>
          </STable>
        </div>
        {hasFixedAmountRows && (
          <div className="tw-col-span-2">
            <SNotification
              icon={null}
              title="Amount is applied in each selected plan's billing currency. For example, 10 becomes $10 for USD plans, EUR 10 for EUR plans, etc."
              variant="warning"
            />
          </div>
        )}
      </>
    );
  };

  const renderAddonScopeRadios = () => {
    if (action.type !== OptimizationActionType.ADDON_PRICE_ADJUSTMENT) {
      return null;
    }

    return (
      <div className="tw-col-span-2">
        <SRadioGroup
          className="[&_.radio-option-label]:!tw-m-0"
          value={addonScopeForUi ?? OptimizationAddonScope.ALL_ADDONS}
          orientation="horizontal"
          onValueChange={(val) => {
            const nextScope = val as OptimizationAddonScope;
            if (nextScope === addonScopeForUi) {
              return;
            }

            onChange(
              createAddonScopeSwitchAction(
                action as AddonPriceAdjustmentAction,
                nextScope
              )
            );
          }}
          options={[
            {
              value: OptimizationAddonScope.ALL_ADDONS,
              label: 'All Addons',
            },
            {
              value: OptimizationAddonScope.SPECIFIC_ADDONS,
              label: 'Specific Addons',
            },
          ]}
        />
      </div>
    );
  };

  const renderAddonAllFields = () => {
    if (
      action.type !== OptimizationActionType.ADDON_PRICE_ADJUSTMENT ||
      addonScopeForUi !== OptimizationAddonScope.ALL_ADDONS
    ) {
      return null;
    }

    const isPercentage = isAddonPercentageAdjustmentType(
      action.adjustment_type
    );

    return (
      <>
        <div>
          <SSelect
            className="tw-w-full"
            value={action.adjustment_type}
            onValueChange={(val) => {
              onChange(
                produce(action, (draft) => {
                  draft.adjustment_type =
                    val as unknown as OptimizationAddonAdjustmentType;
                })
              );
            }}
            clearable={false}
            searchable={false}
            options={addonAdjustmentTypeOptions}
            label="Addon adjustment type"
          />
        </div>
        <div>
          <SInput
            className="tw-w-full"
            onChange={(e) => {
              const val = parseFloat(e.currentTarget.value);
              onChange(
                produce(action, (draft) => {
                  draft.adjustment_value =
                    Number.isNaN(val) || val < 0 ? 0 : val;
                })
              );
            }}
            value={`${action.adjustment_value ?? 0}`}
            label={isPercentage ? 'Percentage' : 'Adjustment amount'}
            type="number"
            trailingIcon={isPercentage ? 'percent' : undefined}
            append={
              !isPercentage ? (
                <span className="tw-text-sm tw-text-neutral-400">
                  per subscription currency
                </span>
              ) : undefined
            }
          />
        </div>
        {!isPercentage && (
          <div className="tw-col-span-2">
            <SNotification
              icon={null}
              title="Amount is applied in each subscription's billing currency. For example, 10 becomes $10 for USD subscriptions, EUR 10 for EUR subscriptions, etc."
              variant="warning"
            />
          </div>
        )}
      </>
    );
  };

  const renderAddonSpecificTable = () => {
    if (
      action.type !== OptimizationActionType.ADDON_PRICE_ADJUSTMENT ||
      addonScopeForUi !== OptimizationAddonScope.SPECIFIC_ADDONS
    ) {
      return null;
    }

    return (
      <div className="tw-col-span-2">
        <STable layout="fixed">
          <STable.Head>
            <STable.Row>
              <STable.HeaderCell className="tw-w-2/12">
                Addon ID
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-2/12">
                Adjustment Type
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-2/12">Value</STable.HeaderCell>
              <STable.HeaderCell className="tw-w-2/12">
                Current Price
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-2/12">
                New Price
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-1/12" />
            </STable.Row>
          </STable.Head>
          <STable.Body>
            {action.addons?.map((row, idx) => {
              const rowIsPercentage = isAddonPercentageAdjustmentType(
                row.adjustment_type
              );
              const addonIdsTakenByOtherRows = new Set(
                action.addons
                  ?.filter((_, i) => i !== idx)
                  .map((addon) => addon.item_price_id)
                  .filter((id): id is string => Boolean(id)) ?? []
              );

              let rowOptions = allAddonOptions.filter(
                (option) =>
                  option.value === row.item_price_id ||
                  !addonIdsTakenByOtherRows.has(option.value)
              );
              if (
                row.item_price_id &&
                !allAddonOptions.some(
                  (option) => option.value === row.item_price_id
                )
              ) {
                rowOptions = [
                  {
                    value: row.item_price_id,
                    label: row.item_price_id,
                    currencyCode: '',
                  },
                  ...rowOptions,
                ];
              }

              const catalogRow = addonCatalogById(row.item_price_id);
              const currentMajor =
                catalogRow !== undefined
                  ? catalogPriceToMajorUnits(Number(catalogRow.price))
                  : undefined;
              const newMajor = previewNewPriceMajorUnits(
                currentMajor,
                row.adjustment_type,
                row.adjustment_value
              );

              return (
                <STable.Row key={idx}>
                  <STable.Cell>
                    <SSelect
                      options={rowOptions}
                      value={row.item_price_id}
                      onValueChange={(val) => {
                        onChange(
                          produce(action, (draft) => {
                            draft.addons ??= [];
                            draft.addons[idx] ??=
                              OptimizationSliceHelper.createNewAddonAdjustment();
                            draft.addons[idx].item_price_id = val as string;
                          })
                        );
                      }}
                    />
                  </STable.Cell>
                  <STable.Cell>
                    <SSelect
                      options={addonAdjustmentTypeOptions}
                      value={row.adjustment_type}
                      clearable={false}
                      searchable={false}
                      onValueChange={(val) => {
                        onChange(
                          produce(action, (draft) => {
                            draft.addons ??= [];
                            draft.addons[idx] ??=
                              OptimizationSliceHelper.createNewAddonAdjustment();
                            draft.addons[idx].adjustment_type =
                              val as unknown as OptimizationAddonAdjustmentType;
                          })
                        );
                      }}
                    />
                  </STable.Cell>
                  <STable.Cell>
                    <SInput
                      onChange={(e) => {
                        const val = parseFloat(e.currentTarget.value);
                        onChange(
                          produce(action, (draft) => {
                            draft.addons ??= [];
                            draft.addons[idx] ??=
                              OptimizationSliceHelper.createNewAddonAdjustment();
                            draft.addons[idx].adjustment_value =
                              Number.isNaN(val) || val < 0 ? 0 : val;
                          })
                        );
                      }}
                      value={`${row.adjustment_value ?? 0}`}
                      type="number"
                      trailingIcon={rowIsPercentage ? 'percent' : undefined}
                    />
                  </STable.Cell>
                  <STable.Cell>
                    <span className="tw-text-sm tw-text-neutral-800">
                      {formatPriceCell(currentMajor, catalogRow?.currency_code)}
                    </span>
                  </STable.Cell>
                  <STable.Cell>
                    <span className="tw-text-sm tw-text-neutral-800">
                      {formatPriceCell(newMajor, catalogRow?.currency_code)}
                    </span>
                  </STable.Cell>
                  <STable.Cell>{renderAddonMenu(idx)}</STable.Cell>
                </STable.Row>
              );
            })}
          </STable.Body>
          <STable.Footer>
            <STable.Row>
              <STable.Cell colSpan={5}>
                <SButton
                  variant="primary-ghost"
                  icon={<SIcon name="plus" />}
                  onClick={() => {
                    onChange(
                      produce(action, (draft) => {
                        draft.addons ??= [];
                        draft.addons[draft.addons.length] =
                          OptimizationSliceHelper.createNewAddonAdjustment();
                      })
                    );
                  }}
                >
                  Add Another Addon
                </SButton>
              </STable.Cell>
            </STable.Row>
          </STable.Footer>
        </STable>
      </div>
    );
  };

  const renderedActions =
    action.type === OptimizationActionType.PLAN_PRICE_ADJUSTMENT ? (
      <>
        {renderPlanScopeRadios()}
        {planScopeForUi === OptimizationPlanScope.ALL_PLANS
          ? renderPlanAllFields()
          : renderPlanSpecificTable()}
      </>
    ) : action.type === OptimizationActionType.ADDON_PRICE_ADJUSTMENT ? (
      <>
        {renderAddonScopeRadios()}
        {addonScopeForUi === OptimizationAddonScope.ALL_ADDONS
          ? renderAddonAllFields()
          : renderAddonSpecificTable()}
      </>
    ) : action.type === OptimizationActionType.PLAN_CHANGE ? (
      <div className="tw-col-span-2">
        <STable layout="fixed">
          <STable.Head>
            <STable.Row>
              <STable.HeaderCell className="tw-w-5/12">
                Current Plan
              </STable.HeaderCell>
              <STable.HeaderCell className="tw-w-5/12">
                Target Plan
              </STable.HeaderCell>
              <STable.HeaderCell />
            </STable.Row>
          </STable.Head>
          <STable.Body>
            {action.plans?.map((plan, idx) => {
              const currentCurrency = allPlansOptions.find(
                (option) => option.value === plan.current_plan
              )?.currencyCode;
              const targetCurrency = allPlansOptions.find(
                (option) => option.value === plan.target_plan
              )?.currencyCode;

              const currentPlanOptions = targetCurrency
                ? OptimizationActionsHelper.filterPlanOptionsByCurrency(
                    allPlansOptions,
                    targetCurrency
                  )
                : allPlansOptions;

              const targetPlanOptions = currentCurrency
                ? OptimizationActionsHelper.filterPlanOptionsByCurrency(
                    allPlansOptions,
                    currentCurrency
                  )
                : allPlansOptions;

              return (
                <STable.Row key={idx}>
                  <STable.Cell>
                    <SSelect
                      options={currentPlanOptions}
                      value={plan.current_plan}
                      onValueChange={(val) => {
                        onChange(
                          produce(action, (draft) => {
                            draft.plans ??= [];
                            draft.plans[idx] ??=
                              OptimizationSliceHelper.createNewPlanChangePlan();
                            draft.plans[idx].current_plan = val as string;
                          })
                        );
                      }}
                    />
                  </STable.Cell>
                  <STable.Cell>
                    <SSelect
                      options={targetPlanOptions}
                      value={plan.target_plan}
                      onValueChange={(val) => {
                        onChange(
                          produce(action, (draft) => {
                            draft.plans ??= [];
                            draft.plans[idx] ??=
                              OptimizationSliceHelper.createNewPlanChangePlan();
                            draft.plans[idx].target_plan = val as string;
                          })
                        );
                      }}
                    />
                  </STable.Cell>
                  <STable.Cell>{renderMenu(idx)}</STable.Cell>
                </STable.Row>
              );
            })}
          </STable.Body>
          <STable.Footer>
            <STable.Row>
              <STable.Cell colSpan={2}>
                <SButton
                  variant="primary-ghost"
                  icon={<SIcon name="plus" />}
                  onClick={() => {
                    onChange(
                      produce(action, (draft) => {
                        draft.plans ??= [];
                        draft.plans[draft.plans.length] =
                          OptimizationSliceHelper.createNewPlanChangePlan();
                      })
                    );
                  }}
                >
                  Add Another Plan
                </SButton>
              </STable.Cell>
            </STable.Row>
          </STable.Footer>
        </STable>
      </div>
    ) : null;

  return (
    <>
      {!actionsOnly && (
        <>
          <div className="tw-p-6">
            <SInput
              value={action.display_name}
              onChange={(e) => {
                const val = e.currentTarget.value;
                onChange(
                  produce(action, (draft) => {
                    draft.display_name = val;
                  })
                );
              }}
              label="Rule Name"
              placeholder="Enter rule name..."
            />
          </div>
          <div className="tw-p-6">
            <ConditionsGroup path={['audience']} maxLevels={1} />
          </div>
        </>
      )}
      <div className={cs({ 'tw-p-6': !actionsOnly })}>
        {!actionsOnly && <div className="tw-mb-sm tw-text-xs">Actions</div>}
        <div className="tw-grid tw-grid-cols-2 tw-gap-sm">
          <div>
            <SSelect
              className="tw-w-full"
              value={action.type}
              onValueChange={(val) => {
                const nextType = val as OptimizationActionType;
                if (nextType === action.type) {
                  return;
                }

                setPendingActionChange(
                  createActionTypeSwitchChange(action, nextType)
                );
              }}
              clearable={false}
              searchable={false}
              options={[
                {
                  label: 'Plan Price Adjustment',
                  value: OptimizationActionType.PLAN_PRICE_ADJUSTMENT,
                },
                {
                  label: 'Plan Change',
                  value: OptimizationActionType.PLAN_CHANGE,
                },
                {
                  label: 'Addon Price Adjustment',
                  value: OptimizationActionType.ADDON_PRICE_ADJUSTMENT,
                },
              ]}
              label="Type"
            />
          </div>
          {renderedActions}
        </div>
      </div>

      <SModal
        open={pendingActionChange !== null}
        onOpenChange={handlePendingActionModalOpenChange}
      >
        <SModal.Content size="small">
          <SModal.Header showCloseButton>
            <SModal.Title>{pendingActionChange?.prompt.title}</SModal.Title>
            <SModal.Description>
              {pendingActionChange?.prompt.description}
            </SModal.Description>
          </SModal.Header>
          <SModal.Footer className="!tw-flex-row !tw-justify-end">
            <SButton
              variant="neutral"
              onClick={handleCancelPendingActionChange}
            >
              Cancel
            </SButton>
            <SButton
              variant="danger"
              onClick={handleConfirmPendingActionChange}
            >
              {pendingActionChange?.prompt.confirmLabel ?? 'Continue'}
            </SButton>
          </SModal.Footer>
        </SModal.Content>
      </SModal>
    </>
  );
};
